import styled from "@emotion/styled";
import { Button } from "../../components/common/Button";
import { ModalLayout } from "../../components/common/ModalLayout";
import theme from "../../theme";
import { useState } from "react";
import { useWindowSize } from "@reactuses/core";
import usePostDetectDownload from "../../hooks/usePostDetectDownload";
import { PostDetectDownloadParams } from "../../types/types";
import { useSnackbar } from "notistack";
import { Box } from "@mui/material";
import { CheckBoxEmpty, CheckBoxFull } from "../../assets/icons/icons";

const { md } = theme.screen;

export const FileDownModal: React.FC<{
  open: boolean;
  onPressNo: () => void;
  startDate: Date;
  endDate: Date;
}> = (props) => {
  const { open, onPressNo, startDate, endDate } = props;
  const { width } = useWindowSize();
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState<PostDetectDownloadParams>({
    endDate: endDate.toISOString().split("T")[0],
    isDlDailyDeviceRpt: "Y",
    isDlDailyEventRpt: "Y",
    isDlDailyMobilityRpt: "Y",
    isDlDeviceAccidentRpt: "Y",
    isDlHourlyMobilityRpt: "Y",
    startDate: startDate.toISOString().split("T")[0],
  });

  // 기기가 터치 이벤트를 지원하는지 확인하여 모바일 여부 판단
  const isMobile =
    "ontouchstart" in window || navigator.maxTouchPoints > 0;

  const { mutate: postDown, isPending: downIsPending } =
    usePostDetectDownload();

  const handlePost = () => {
    postDown(form, {
      onSuccess: async (data) => {
        // 만약 data가 없다면 에러 메시지를 표시하고 종료
        if (!data.data)
          enqueueSnackbar(`다운로드 가능한 파일이 없습니다.`, {
            variant: "error",
          });

        // Blob 데이터를 받아서 Blob URL을 생성 (파일을 다운로드할 수 있는 URL)
        const url = window.URL.createObjectURL(data.data);
        // <a> 태그를 동적으로 생성하여 다운로드 링크를 만듦
        const a = document.createElement("a");
        a.href = url;

        // content-disposition 헤더에서 파일명 추출
        const fileNameMatch = data.headers["content-disposition"].match(
          /filename\*=UTF-8''(.+)/
        );
        // 기본 파일명 설정
        let fileName = "default_filename.xlsx"; // 기본 파일 이름
        if (fileNameMatch && fileNameMatch[1]) {
          // 추출된 URL 인코딩된 파일명을 decodeURIComponent로 디코딩
          fileName = decodeURIComponent(fileNameMatch[1]);
        }
        a.download = fileName ? fileName : `download_file`;

        document.body.appendChild(a);
        a.click();

        // Blob URL을 해제하여 메모리에서 정리
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000);
        a.remove();

        onPressNo();
        // 다운로드 성공 메시지를 로그인 유저에게 표시
        enqueueSnackbar(`파일 다운로드 성공`, {
          variant: "success",
        });
        return data;
      },
    });
  };

  const onClickBox = (type: keyof PostDetectDownloadParams) => {
    setForm((prev) => {
      if (prev[type] === "Y") {
        return { ...prev, [type]: "N" };
      } else if (prev[type] === "N") {
        return { ...prev, [type]: "Y" };
      } else {
        return { ...prev, [type]: "Y" }; // prev[type]이 undefined일 때 기본값 설정
      }
    });
  };

  // useEffect(() => {
  //   if(isSuccess) {

  //   }
  // },[isSuccess])

  // console.log(form.startDate);
  // console.log(form.endDate);
  // console.log(form);

  return (
    <ModalLayout
      open={open}
      onClose={onPressNo}
      width="400px"
      title="감지내역 통계 다운로드"
    >
      <Box
        sx={{
          padding: width > md ? "10px 40px" : "0px",
          display: "flex",
          flexDirection: "column",
          gap: width > md ? "32px" : "16px",
        }}
      >
        <Box>
          <ContentsTitle>기록 기간</ContentsTitle>
          <Box
            sx={{
              color: theme.color.neutral_600,
              padding: "5px 10px",
              fontSize:
                width > md
                  ? theme.fontSize.BOTTOM_SHEET_1
                  : theme.fontSize.FONT_1,
            }}
          >
            {`${form.startDate} ~ ${form.endDate}`}
          </Box>
        </Box>

        {/* 파일 형식 정보 */}
        <Box>
          <ContentsTitle>파일 형식</ContentsTitle>
          <Box
            sx={{
              color: theme.color.neutral_600,
              padding: "5px 10px",
              fontSize:
                width > md
                  ? theme.fontSize.BOTTOM_SHEET_1
                  : theme.fontSize.FONT_1,
            }}
          >
            Excel 문서(.xlsx)
          </Box>
        </Box>

        {/* 포함 정보 체크박스 */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: width > md ? "8px" : "20px",
          }}
        >
          <ContentsTitle>포함 정보</ContentsTitle>
          {/* 일별 농기계 운행 */}
          <CheckWrapper
            // 모바일일 때는 onTouchEnd, 웹일 때는 onClick
            onClick={
              !isMobile
                ? () => onClickBox("isDlDailyMobilityRpt")
                : undefined
            }
            onTouchEnd={
              isMobile
                ? () => onClickBox("isDlDailyMobilityRpt")
                : undefined
            }
          >
            <CheckBoxIconWrapper>
              {form.isDlDailyMobilityRpt === "Y" ? (
                <CheckBoxFull />
              ) : (
                <CheckBoxEmpty />
              )}
            </CheckBoxIconWrapper>
            <CheckText>일별 농기계 운행</CheckText>
          </CheckWrapper>

          {/* 시간대별 농기계 운행 */}
          <CheckWrapper
            // 모바일일 때는 onTouchEnd, 웹일 때는 onClick
            onClick={
              !isMobile
                ? () => onClickBox("isDlHourlyMobilityRpt")
                : undefined
            }
            onTouchEnd={
              isMobile
                ? () => onClickBox("isDlHourlyMobilityRpt")
                : undefined
            }
          >
            <CheckBoxIconWrapper>
              {form.isDlHourlyMobilityRpt === "Y" ? (
                <CheckBoxFull />
              ) : (
                <CheckBoxEmpty />
              )}
            </CheckBoxIconWrapper>
            <CheckText>시간대별 농기계 운행</CheckText>
          </CheckWrapper>

          {/* 일별 이벤트 현황 */}
          <CheckWrapper // 모바일일 때는 onTouchEnd, 웹일 때는 onClick
            onClick={
              !isMobile ? () => onClickBox("isDlDailyEventRpt") : undefined
            }
            onTouchEnd={
              isMobile ? () => onClickBox("isDlDailyEventRpt") : undefined
            }
          >
            <CheckBoxIconWrapper>
              {form.isDlDailyEventRpt === "Y" ? (
                <CheckBoxFull />
              ) : (
                <CheckBoxEmpty />
              )}
            </CheckBoxIconWrapper>
            <CheckText>일별 이벤트 현황</CheckText>
          </CheckWrapper>

          {/* 단말별 운행 현황 */}
          <CheckWrapper // 모바일일 때는 onTouchEnd, 웹일 때는 onClick
            onClick={
              !isMobile
                ? () => onClickBox("isDlDailyDeviceRpt")
                : undefined
            }
            onTouchEnd={
              isMobile ? () => onClickBox("isDlDailyDeviceRpt") : undefined
            }
          >
            <CheckBoxIconWrapper>
              {form.isDlDailyDeviceRpt === "Y" ? (
                <CheckBoxFull />
              ) : (
                <CheckBoxEmpty />
              )}
            </CheckBoxIconWrapper>
            <CheckText>단말별 운행 현황</CheckText>
          </CheckWrapper>

          {/* 사고 발생 전파시간 */}
          <CheckWrapper
            // 모바일일 때는 onTouchEnd, 웹일 때는 onClick
            onClick={
              !isMobile
                ? () => onClickBox("isDlDeviceAccidentRpt")
                : undefined
            }
            onTouchEnd={
              isMobile
                ? () => onClickBox("isDlDeviceAccidentRpt")
                : undefined
            }
          >
            <CheckBoxIconWrapper>
              {form.isDlDeviceAccidentRpt === "Y" ? (
                <CheckBoxFull />
              ) : (
                <CheckBoxEmpty />
              )}
            </CheckBoxIconWrapper>
            <CheckText>사고 발생 전파시간</CheckText>
          </CheckWrapper>
        </Box>
      </Box>

      <BtnWrapper>
        <Button
          onClick={() => handlePost()}
          bgColor={theme.statusColor.MAIN}
          hoverColor={theme.statusColor.MAIN_HOVER}
          isLoading={downIsPending}
        >
          {width <= md ? "다운" : "다운로드"}
        </Button>
        <Button
          bgColor={theme.statusColor.CLOSE}
          onClick={onPressNo}
          hoverColor={theme.statusColor.CLOSE_HOVER}
        >
          취소
        </Button>
      </BtnWrapper>
    </ModalLayout>
  );
};

const BtnWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 20px;
  margin: 0 50px;
  margin-top: 20px;

  button {
    width: 270px;
  }
  @media (max-width: ${md}px) {
    margin: 0px;
    margin-top: 20px;
  }
`;

const CheckWrapper = styled.button`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 5px 10px;
  border-radius: 10px;
  cursor: pointer;
  &:hover {
    background-color: ${theme.color.neutral_100};

    @media (max-width: ${md}px) {
      background-color: transparent;
    }
  }

  @media (max-width: ${md}px) {
    padding: 0px 10px;
  }
`;

const CheckText = styled.span`
  font-weight: 400;
  line-height: 15px;
  color: ${theme.color.neutral_600};

  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;

  @media (max-width: ${md}px) {
    font-size: ${theme.fontSize.FONT_1};
  }
`;

const CheckBoxIconWrapper = styled.div`
  transform: translateY(1px);
  cursor: pointer;
`;

const ContentsTitle = styled.h5`
  font-size: ${theme.fontSize.BOTTOM_SHEET_1};
  padding: 5px 0px;

  @media (max-width: ${md}px) {
    font-size: ${theme.fontSize.FONT_1};
  }
`;
